import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

interface PageHeroProps {
  image?: string;
  video?: string;
  icon?: React.ElementType;
  title: string;
  subtitle?: string;
  breadcrumbs?: { label: string; to?: string }[];
  actions?: React.ReactNode;
  overlay?: "dark" | "light" | "gradient";
  height?: "sm" | "md" | "lg";
}

const HEIGHTS = {
  sm: "h-32",
  md: "h-44",
  lg: "h-56",
};

export function PageHero({
  image,
  video,
  icon: Icon,
  title,
  subtitle,
  breadcrumbs,
  actions,
  overlay = "gradient",
  height = "md",
}: PageHeroProps) {
  const overlayClass =
    overlay === "dark"
      ? "bg-black/50"
      : overlay === "light"
        ? "bg-white/40 backdrop-blur-sm"
        : "bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent";

  return (
    <div
      className={`relative ${HEIGHTS[height]} rounded-2xl overflow-hidden mb-6 group`}
    >
      {video ? (
        <video
          src={video}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : image ? (
        <img
          src={image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
      )}

      <div className={`absolute inset-0 ${overlayClass}`} />

      <div className="relative h-full flex flex-col justify-end p-6">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1 text-xs text-white/70 mb-2">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight size={12} className="text-white/40" />}
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="hover:text-white transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-white/90 font-medium">
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
        )}

        <div className="flex items-end justify-between gap-4">
          <div className="flex items-center gap-4">
            {Icon && (
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <Icon size={24} className="text-white" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white font-display drop-shadow-sm">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-white/70 mt-0.5 drop-shadow-sm">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
